import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputMask } from "primereact/inputmask";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { RadioButton } from "primereact/radiobutton";
import { classNames } from "primereact/utils";
import { FC, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { WEBSITE_INQUIRY_TYPES } from "../../../../constants/constants";
import { useResponsive } from "../../../../responsive/ResponsiveProvider";
import { BusinessHomeProfile } from "../../../../models/businessHomeProfile";
import { HeaderSection } from "../../../../models/manageWebSite";
import { scrollToElement } from "../../../../utils/stringUtils";
import GoogleMapEmbed from "../../../../components/general/googleMap";
import {
  appAccentPanelStyle,
  appDialogContentStyle,
  appDialogHeaderStyle,
  appDialogStyle,
  appFormSectionStyle,
  appMutedTextStyle,
  renderDialogFooter,
  renderSurfaceHeader,
} from "../../../../components/general/uiSurfaceStyles";

interface Section6Props {
  data: BusinessHomeProfile | null;
  headerData: HeaderSection | null;
}

const Section6: FC<Section6Props> = ({ data, headerData }) => {
  const [dialogVisible, setDialogVisible] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const mobile = useResponsive().isMobile;
  const [demoDialogVisible, setDemoDialogVisible] = useState(false);

  const defaultValues = {
    fullName: "",
    email: "",
    phoneNumber: "",
    message: "",
    inquiryType: ""
  };

  const { control, formState: { errors }, handleSubmit, reset } = useForm({ defaultValues });

  const getFormErrorMessage = (name: keyof typeof defaultValues) => {
    return errors[name] && <small className="p-error">{errors[name]?.message}</small>;
  }


  return (
    <div id="13" style={{ width: "100%", background: "linear-gradient(180deg, #fffaf3 0%, #f8fbff 100%)", padding: "3rem 1.25rem" }} className="gap-3 d-flex justify-content-center align-items-center flex-column">
      <div style={{ maxWidth: "1180px", width: "100%", margin: "0 auto", textAlign: "center" }}>
      <div className="mb-4" style={{ textAlign: "center" }}>
        <div style={{ color: "#9a3412", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", fontSize: "0.8rem" }}>
          Contact and location
        </div>
        <div style={{ color: "#0f172a", fontSize: mobile ? "1.85rem" : "2.35rem", fontWeight: 800, marginTop: "0.75rem" }}>
          Make it easy for families to reach out
        </div>
        <div style={{ color: "#5f7488", lineHeight: 1.8, maxWidth: "760px", margin: "0.85rem auto 0" }}>
          A clear final section builds trust fast. Keep your address, map, phone number, and inquiry flow polished and easy to scan.
        </div>
      </div>
      <div className="m-0 mb-4 flex align-items-center justify-content-center flex-wrap">
        {[
          { id: 11, label: "Services" },
          { id: 12, label: "Gallery" },
        ].map((obj) => (
          <span key={obj.id} style={{ color: "#185da2", cursor: "pointer", margin: "0.75em", fontSize: "1rem", fontWeight: 700 }} onClick={() => scrollToElement(`${obj.id}`)} >
            {obj.label}
          </span>
        ))}
        <span>
          <span style={{ color: "#185da2", cursor: "pointer", margin: "0.75em", fontSize: "1rem", fontWeight: 700 }} onClick={(e) => {
            e.preventDefault();
            setDialogVisible(true);
          }} >
            Contact us
          </span>
        </span>
      </div>

      <div className="grid justify-content-center">
        <div className="col-12 lg:col-7">
          <div style={{ background: "#ffffff", border: "1px solid #d9e8f5", borderRadius: "28px", padding: "1.35rem", boxShadow: "0 22px 44px rgba(15,61,99,0.08)", height: "100%" }} className="flex align-items-center justify-content-center flex-column">
          <GoogleMapEmbed lat={data?.latitude} lng={data?.longitude} />
          <Button label="Open contact form" severity="warning" className="mt-3" onClick={(e) => {
            e.preventDefault();
            setDialogVisible(true);
          }} />
          </div>
        </div>
        <div className="col-12 lg:col-5">
          <div style={{ background: "linear-gradient(135deg, #0f3d63 0%, #185da2 100%)", borderRadius: "28px", padding: "1.6rem", boxShadow: "0 26px 50px rgba(15,61,99,0.14)", height: "100%", textAlign: "center", margin: "0 auto", width: "100%" }} className="flex align-items-center justify-content-center flex-column">
          <div style={{ color: "rgba(255,255,255,0.76)", fontSize: "0.82rem", textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.12em" }}>Contact</div>
          <div className="mt-3" style={{ color: "#ffffff", fontSize: "2rem", fontWeight: 800 }}>{data?.businessName}</div>
          <div className="mt-3" style={{ color: "rgba(255,255,255,0.86)", lineHeight: 1.8 }}>{data?.formattedAddress}</div>
          <div className="mt-3" style={{ color: "rgba(255,255,255,0.76)", lineHeight: 1.8 }}>
            Invite families, hospital discharge teams, and referral partners to contact the home with confidence.
          </div>
          <div className="mt-4 flex align-items-center justify-content-center" style={{ color: "white" }}>
            <i className="pi pi-phone" style={{ marginRight: "0.5em" }}></i>
            <span style={{ color: "white", cursor: "pointer", textDecoration: "underline" }}
              onClick={() => window.location.href = `tel:${data?.businessPhoneNumber}`}>
              <InputMask mask="(999) 999-9999" value={data?.businessPhoneNumber} disabled style={{ border: "none", color: "white", backgroundColor: "transparent", textDecoration: "underline", cursor: "pointer" }} />
            </span>
          </div>
          <div className="mt-4" style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.16)", borderRadius: "20px", padding: "0.9rem 1rem", color: "#ffffff", lineHeight: 1.7, width: "100%" }}>
            Simple, fast, family-friendly contact experience
          </div>
          </div>
        </div>
      </div>
      </div>

      <Dialog visible={dialogVisible} modal
        header={renderSurfaceHeader("Contact Us", "Preview the public contact flow and form experience.", "pi pi-envelope")}
        style={mobile ? { width: "90%" } : appDialogStyle}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        onHide={() => { setDialogVisible(false); }}>
        <div className="grid">
          <div className="col p-3">
            <div style={appFormSectionStyle}>
            <GoogleMapEmbed lat={data?.latitude} lng={data?.longitude} />

            <i className="pi pi-map-marker" style={{ fontSize: "2em", color: "black" }}></i>
            <span>{data?.formattedAddress}</span>
            </div>
          </div>
          <div className="col p-3">
            <div style={appAccentPanelStyle} className="mb-3">
              <div style={{ fontWeight: 700, color: "#9a3412" }}>Contact preview</div>
              <div className="mt-2" style={appMutedTextStyle}>This simulates the inquiry form a website visitor would fill out.</div>
            </div>
            <form style={appFormSectionStyle}>
              <div className="field">
                <span className="p-float-label">
                  <Controller name="fullName" control={control} rules={{
                    required: 'Name is required.',
                    maxLength: { value: 100, message: 'Name must be at most 100 characters long.' }
                  }} render={({ field, fieldState }) => (
                    <InputText id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                  )} />
                  <label htmlFor="fullName" className={classNames({ 'p-error': errors.fullName })}>Full Name</label>
                </span>
                {getFormErrorMessage('fullName')}
              </div>

              <div className="field">
                <span className="p-float-label">
                  <Controller name="email" control={control} rules={{
                    required: 'Email is required.',
                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Invalid email address.' }
                  }} render={({ field, fieldState }) => (
                    <InputText id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                  )} />
                  <label htmlFor="email" className={classNames({ 'p-error': errors.email })}>Email</label>
                </span>
                {getFormErrorMessage('email')}
              </div>

              <div className="field">
                <span className="p-float-label">
                  <Controller name="phoneNumber" control={control} rules={{
                    required: 'Phone number is required.',
                    pattern: { value: /^\+?[1-9]\d{1,14}$/, message: 'Invalid phone number.' }
                  }} render={({ field, fieldState }) => (
                    <InputText id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                  )} />
                  <label htmlFor="phoneNumber" className={classNames({ 'p-error': errors.phoneNumber })}>Phone Number</label>
                </span>
                {getFormErrorMessage('phoneNumber')}
              </div>

              <div className="field">
                <span className="p-float-label">
                  <Controller name="message" control={control} rules={{
                    required: 'Message is required.',
                    maxLength: { value: 500, message: 'Message must be at most 500 characters long.' }
                  }} render={({ field, fieldState }) => (
                    <InputTextarea rows={5} style={{ width: "100%" }} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                  )} />
                  <label htmlFor="message" className={classNames({ 'p-error': errors.message })}>Message</label>
                </span>
                {getFormErrorMessage('message')}
              </div>

              <div>
                <h4>Inquiry Type:</h4>
                <div className="p-field-radiobutton">
                  {WEBSITE_INQUIRY_TYPES.map((option) => (
                    <div key={option.value} className="p-field-radiobutton">
                      <Controller
                        name="inquiryType"
                        control={control}
                        rules={{ required: 'Please select an option.' }}
                        render={({ field }) => (
                          <RadioButton
                            {...field}
                            inputId={option.value}
                            value={option.value}
                            checked={field.value === option.value}
                            onChange={(e) => field.onChange(e.value)}
                          />
                        )}
                      />
                      <label htmlFor={option.value}>{option.name}</label>
                    </div>
                  ))}
                </div>
                {getFormErrorMessage('inquiryType')}
              </div>

              {renderDialogFooter({
                errorMessage,
                loading,
                onCancel: () => setDialogVisible(false),
                onSubmit: handleSubmit(() => setDemoDialogVisible(true)),
                submitLabel: "Preview Submit",
              })}
            </form>
          </div>
        </div>
      </Dialog >

      <Dialog visible={demoDialogVisible} modal
        header={renderSurfaceHeader("Demo Notification", "Shows the final step website visitors would experience.", "pi pi-info-circle")}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        onHide={() => { setDemoDialogVisible(false); }}>
        <div style={appFormSectionStyle}>
          Visitor will be able to submit a request in this way.
        </div>
      </Dialog>
    </div>
  );
};

export default Section6;
